<template>
<article
  role="document"
  class="modal-dialog modal-dialog-centered"
  :class="modalClasses"
>
  <div class="modal-content" v-bind="attrs">
    <div class="modal-header" ref="header">
      <template v-if="showCloseButton">
        <CloseModalButton class="btn-close float-end" />
      </template>
      <div class="header-content me-auto flex-grow-1 d-flex flex-row align-items-center justify-content-start gap-2">
        <slot name="header" :id="labelId">
          <h1 class="modal-title h5" :id="unref(labelId)">
            <slot name="title"></slot>
          </h1>
        </slot>
      </div>
    </div>
    <slot name="contentAndFooter">
      <div class="modal-body">
        <div v-if="$slots.visually_hidden_header" class="visually-hidden">
          <slot name="visually_hidden_header"></slot>
        </div>
        <div v-if="$slots.always_visible">
          <slot name="always_visible" />
        </div>
        <slot name="contentPre"/>
        <slot name="content-pre"/>
        <slot name="content" />
        <slot />
        <slot name="ps" />
      </div>
      <div class="modal-footer">
        <slot name="footer"></slot>
      </div>
    </slot>
  </div>
</article>
</template>

<script setup lang="ts">
import { computed, inject, ref, unref, useAttrs } from 'vue'
import { useRoute } from 'vue-router'

import { useModalData } from '#src/js/modalData.mts'
import { useUpdateWatcher } from 'updates/vue/updateConsumer.mts'

import CloseModalButton from './CloseModalButton.vue'

const attrs = useAttrs()

const route = useRoute()

export interface Props {
  wrapper?: 'page' | 'modal' | undefined
}

const props = withDefaults(
  defineProps<Props>(),
  {
    wrapper: undefined,
  },
)

const { labelId, showCloseButton, transitionAppear, defaultModalSizeClass } = useModalData()

const injectedWrapper = inject<'page' | 'modal' | null>('wrapper', null)
const _wrapper = computed<'page' | 'modal'>(() => {
  const { wrapper: _propsWrapper } = props
  const _injectedWrapper = injectedWrapper ? unref(injectedWrapper) : null
  const result = _propsWrapper ?? _injectedWrapper ?? 'page'
  return result
})
const injectedModalClasses = inject<string[]>('modalClasses', [])

const modalClasses = computed<string[]>(() => {
  const { meta } = route ?? {}
  const _injectedModalClasses = unref(injectedModalClasses)

  const classes: string[] = []

  if (meta?.modalClasses) {
    const _classes: string[] = meta?.modalClasses as unknown as string[]
    classes.push(..._classes)
  }

  if (injectedModalClasses) {
    classes.push(..._injectedModalClasses)
  }

  if (classes.length === 0) {
    // defaultModalSizeClass
    // null value means disable.
    // undefined means use default (modal-xl).
    const _class = unref(defaultModalSizeClass)
    const _default = _class === undefined ? 'modal-xl' : _class
    if (_default !== null) {
      classes.push(_default)
    }
  }

  const { modalOptions } = (route.meta ?? {}) as { modalOptions?: any }
  if (modalOptions?.scrollable ?? true) {
    classes.push('modal-dialog-scrollable')
  }

  return classes
})

const updateElementRef = ref<HTMLElement>()
useUpdateWatcher(updateElementRef, undefined)

defineOptions({
  inheritAttrs: false,
})

defineExpose({

})
</script>
<style scoped>

.modal-header {
  align-items: center;
  flex-direction: row-reverse;
}
</style>
